import { api, handleApiError } from '../client'
import type {
  TenantAnalyticsOverview,
  TemplateAnalytics,
  TemplateUsageEvent,
  TopTemplateAnalytics,
  TopUserAnalytics,
  UsageTrendData,
  HeatmapData,
  DepartmentUsage,
  FieldCompletionData,
  AnalyticsFilters,
} from '../types'

// Matches backend DashboardStatsDto
export interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalTemplates: number
  totalAssets: number
  totalDocumentsGenerated: number
  totalAiTokensUsed: number
  storageUsedBytes: number
  activeWorkflows: number
  userChangePercent: number
  templateChangePercent: number
  documentChangePercent: number
  storageChangePercent: number
}

// Matches backend TopTemplateDto
export interface TopTemplate {
  id: string
  name: string
  category: string | null
  uses: number
  downloads: number
  successRate: number
  feedbackScore: number | null
}

// Matches backend HourlyActivityDto
export interface HourlyActivity {
  hour: number
  activeUsers: number
  documentsGenerated: number
  aiRequests: number
}

// Matches backend ActivityLogDto
export interface ActivityLog {
  id: string
  userId: string
  userName: string
  action: string
  entityType: string | null
  entityId: string | null
  entityName: string | null
  timestamp: string
  details: string | null
}

// Matches backend UsageTrendDto
export interface UsageTrend {
  metricName: string
  dataPoints: { timestamp: string; value: number; label: string | null }[]
  totalValue: number
  changePercent: number
}

// Matches backend TopAssetDto
export interface TopAsset {
  id: string
  name: string
  type: string
  uses: number
  downloads: number
  sizeBytes: number
}

// Matches backend UserActivityDto
export interface UserActivity {
  userId: string
  userName: string
  email: string | null
  templatesCreated: number
  documentsGenerated: number
  assetsUploaded: number
  lastActiveAt: string
}

// Matches backend UsageMetricSummaryDto
export interface UsageMetricSummary {
  metricType: string
  currentValue: number
  limit: number | null
  usagePercentage: number | null
  unit: string | null
  isOverLimit: boolean
  isApproachingLimit: boolean
}

// Matches backend StorageAnalyticsDto
export interface StorageAnalytics {
  totalBytesUsed: number
  totalBytesLimit: number
  usagePercentage: number
  bytesByFileType: Record<string, number>
  totalFiles: number
  averageFileSize: number
  largestFileSize: number
}

// Matches backend AnalyticsDashboardDto
export interface AnalyticsDashboardResponse {
  stats: DashboardStats
  usageTrends: UsageTrend[]
  topTemplates: TopTemplate[]
  topAssets: TopAsset[]
  topUsers: UserActivity[]
  hourlyActivity: HourlyActivity[]
  recentActivity: ActivityLog[]
  usageMetrics: UsageMetricSummary[]
}

// Helper to build query string from filters
function buildFilterQuery(filters?: AnalyticsFilters): string {
  if (!filters) return ''
  const params = new URLSearchParams()
  if (filters.startDate) params.append('startDate', filters.startDate)
  if (filters.endDate) params.append('endDate', filters.endDate)
  if (filters.templateId) params.append('templateId', filters.templateId)
  if (filters.period) params.append('period', filters.period)
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const analyticsService = {
  // Existing methods
  async getAnalytics(): Promise<AnalyticsDashboardResponse> {
    try {
      const response = await api.get<AnalyticsDashboardResponse>('/v1/analytics')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getStats(): Promise<DashboardStats> {
    try {
      const response = await api.get<DashboardStats>('/v1/analytics/stats')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Overview (uses main dashboard for tenant-wide overview)
  async getTemplateAnalyticsOverview(filters?: AnalyticsFilters): Promise<TenantAnalyticsOverview> {
    try {
      // Use the main dashboard endpoint and extract overview stats
      const params: Record<string, string | number | undefined> = {}
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      const response = await api.get<AnalyticsDashboardResponse>('/v1/analytics', { params })
      const stats = response.data.stats

      // Map dashboard stats to tenant analytics overview
      return {
        totalTemplates: stats.totalTemplates,
        activeTemplates: stats.totalTemplates, // Dashboard doesn't have active count separately
        totalViews: stats.totalDocumentsGenerated, // Using documents as proxy
        totalDownloads: 0, // Not tracked in dashboard stats
        totalGenerations: stats.totalDocumentsGenerated,
        uniqueUsers: stats.activeUsers,
        viewsTrend: stats.documentChangePercent,
        downloadsTrend: 0,
        generationsTrend: stats.documentChangePercent,
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - All templates (uses top templates as proxy)
  async getTemplateAnalytics(filters?: AnalyticsFilters): Promise<TemplateAnalytics[]> {
    try {
      const params: Record<string, string | number | undefined> = { count: 50 }
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      const response = await api.get<TopTemplate[]>('/v1/analytics/templates/top', { params })

      // Map TopTemplate to TemplateAnalytics
      return response.data.map((t) => ({
        templateId: t.id,
        templateName: t.name,
        templateThumbnail: undefined,
        viewCount: t.uses,
        downloadCount: t.downloads,
        generationCount: t.uses,
        uniqueUsers: 0,
        averageDurationMs: 0,
        fieldCompletionRate: t.successRate,
        trend: 0,
      }))
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Single template
  async getTemplateAnalyticsById(id: string, filters?: AnalyticsFilters): Promise<TemplateAnalytics> {
    try {
      const query = buildFilterQuery(filters)
      const response = await api.get<TemplateAnalytics>(`/v1/analytics/templates/${id}${query}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Events for a template
  // Note: This endpoint doesn't exist yet on the backend, returns empty array
  async getTemplateEvents(_id: string, _filters?: AnalyticsFilters): Promise<TemplateUsageEvent[]> {
    // TODO: Implement GET /v1/analytics/templates/{id}/events endpoint on backend
    // For now, return empty array since the endpoint only supports POST for recording events
    return []
  },

  // Template Analytics - Top templates
  async getTopTemplates(filters?: AnalyticsFilters, limit = 10): Promise<TopTemplateAnalytics[]> {
    try {
      const params: Record<string, string | number | undefined> = { count: limit }
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      const response = await api.get<TopTemplate[]>('/v1/analytics/templates/top', { params })

      // Map TopTemplate to TopTemplateAnalytics
      return response.data.map((t) => ({
        templateId: t.id,
        templateName: t.name,
        templateThumbnail: undefined,
        count: t.uses + t.downloads,
        trend: 0, // Backend doesn't provide trend data
      }))
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Top users
  async getTopUsers(_filters?: AnalyticsFilters, limit = 10): Promise<TopUserAnalytics[]> {
    try {
      // Note: Backend endpoint doesn't support date filtering currently
      const params: Record<string, string | number | undefined> = { count: limit }

      const response = await api.get<UserActivity[]>('/v1/analytics/users/top', { params })

      // Map UserActivity to TopUserAnalytics
      return response.data.map((u) => ({
        userId: u.userId,
        userName: u.userName,
        userEmail: u.email ?? '',
        userAvatar: undefined,
        count: u.templatesCreated + u.documentsGenerated + u.assetsUploaded, // Total activity
      }))
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Usage trends (uses dashboard data)
  async getUsageTrends(filters?: AnalyticsFilters): Promise<UsageTrendData[]> {
    try {
      // Use the main dashboard endpoint which includes usage trends
      const params: Record<string, string | number | undefined> = {}
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      const response = await api.get<AnalyticsDashboardResponse>('/v1/analytics', { params })

      // Map UsageTrend[] to UsageTrendData[]
      // Dashboard returns trends in a different format
      const trends = response.data.usageTrends || []

      // Generate daily data points from the trends
      const viewsTrend = trends.find((t) => t.metricName === 'DocumentsGenerated')
      if (viewsTrend && viewsTrend.dataPoints.length > 0) {
        return viewsTrend.dataPoints.map((dp) => ({
          date: dp.timestamp,
          views: dp.value,
          downloads: 0,
          generations: dp.value,
        }))
      }

      // Fallback: generate empty trend data for the date range
      return []
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Heatmap data
  async getHeatmapData(filters?: AnalyticsFilters): Promise<HeatmapData[]> {
    try {
      const params: Record<string, string | number | undefined> = {}
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate
      if (filters?.templateId) params.templateId = filters.templateId

      // Correct endpoint path
      const response = await api.get<HeatmapData[]>('/v1/analytics/heatmap', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Department usage
  async getDepartmentUsage(filters?: AnalyticsFilters): Promise<DepartmentUsage[]> {
    try {
      const params: Record<string, string | number | undefined> = {}
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      const response = await api.get<DepartmentUsage[]>('/v1/analytics/departments', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Field completion rates
  async getFieldCompletionRates(templateId: string): Promise<FieldCompletionData[]> {
    try {
      const response = await api.get<FieldCompletionData[]>(
        `/v1/analytics/templates/${templateId}/field-completion`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Template Analytics - Export
  async exportAnalytics(filters?: AnalyticsFilters, format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    try {
      // Export uses POST with a body
      const request = {
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        format: format,
      }
      const response = await api.post<Blob>('/v1/analytics/export', request, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // Storage Analytics
  async getStorageAnalytics(): Promise<StorageAnalytics> {
    try {
      const response = await api.get<StorageAnalytics>('/v1/analytics/storage')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
